import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit,
  ViewChild
} from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { WindowRefService } from '../shared/window.service';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { version } from '../../environments/version';
import { WeatherUtils } from '../weather/weather-utils';
import { WeatherService } from '../weather/weather.service';
import { AppMessage } from '../shared/app-message/app-message';
import { StateService } from '../shared/state/state.service';
import { UserSettingState } from '../shared/state/user-setting.state';
import { UserSettingsComponent } from '../components/user-settings/user-settings.component';
import { DomSanitizer } from '@angular/platform-browser';
import { filter, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../components/general/base/base.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AppStabilityService } from '../shared/app-stability.service';

/**
 * Component for map toolbar
 */
@Component({
  selector: 'qm-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input()
  public injectedComponent: Component;
  /**
   * Result overlay reference
   */
  @ViewChild('template')
  private template: any;
  /**
   * user settings component
   */
  @ViewChild(UserSettingsComponent, { static: true })
  public userSettings: UserSettingsComponent;
  /**
   * Current date
   * @type {string}
   */
  public clock = new Date();
  /**
   * Window instance, injected via service for mocking
   */
  private _window: any;
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();
  private clockTimer: any;
  /**
   * Current weather conditions
   */
  public weather: any;
  public statusAlarms: AppMessage[] = [];
  public logoArr: string[];
  public settings: UserSettingState;
  public helpDoc;
  public tenant = this.auth.getTenant();
  closeResult: string;
  public showVivoLink: boolean;

  constructor(
    private readonly modalService: NgbModal,
    readonly windowRef: WindowRefService,
    private readonly weatherService: WeatherService,
    private readonly auth: AuthService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly appStability: AppStabilityService,
    private readonly stateService: StateService,
    private sanitizer: DomSanitizer,
  ) {
    super();
    this.name = 'ToolbarComponent';
    this._window = windowRef.nativeWindow;
    const tenant = this.auth.getTenant();
    if ([2, 3, 13, 15].includes(tenant)) {
      this.helpDoc = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${environment.assetRoot}/manuals/manual_${tenant}.pdf?regoVersion=${version}`
      );
    }
  }

  /**
   * Angular event, run when view is initialized (dom is rendered etc.)
   */
  ngOnInit() {
    this.logoArr = [environment.assetRoot + '/img/mipro_logo_184.png'];
    if (this.auth.getTenant() === 2 && this.isMapPage()) {
      this.logoArr.unshift(environment.assetRoot + '/img/vr_logo_66.png');
    }
    this.settings = this.stateService.getPartOfState('settingsState');
    this.stateService.state$.pipe(takeUntil(this.ngUnsubscribe), filter(o => o.passesFilter('settingsState'))).subscribe(data => {
      if (data.getValue().size > 0) {
        this.settings = data.getValue();
      }
    });
    this.appStability.whenFirstStable().then(() => {
      this.clockTimer = setInterval(() => {
        this.clock = new Date();
        this.changeDetector.markForCheck();
      }, 1000);
    });
    if (this.auth.hasPermission('weather')) {
      this.weatherService.getWeatherForLocation().then(weather => {
        console.log('Current weather:', weather);
        weather.main.temp = Math.floor(weather.main.temp);
        this.weather = weather;
        this.changeDetector.markForCheck();
      });
    }
    this.showVivoLink = this.auth.getTenantConfig().vivo !== undefined;
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    if(this.clockTimer) {
      clearInterval(this.clockTimer);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  openVerticallyCentered(content) {
    this.modalService.open(content, { centered: true });
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  /**
   * Logout function, send logout command to Auth0
   */
  public logOutUser() {
    this.auth.logout();
  }

  public isMapPage() {
    return this._window.location.href.includes('map') || this._window.location.href.includes('home');
  }

  /**
   * Get weather icon class for current weather
   * @returns {string}
   */
  public getIconClass(): string {
    return WeatherUtils.getIconClass(this.weather);
  }
}
